import { z } from "zod";

export const offerSkus = [
  "paper-kingdom-fe",
  "paper-kingdom-unlimited",
  "paper-kingdom-dfy",
  "paper-kingdom-production",
  "paper-kingdom-launch-growth",
  "paper-kingdom-reseller",
] as const;

export const OfferSkuSchema = z.enum(offerSkus);
export type OfferSku = z.infer<typeof OfferSkuSchema>;

export const marketplaceOptions = [
  "Amazon KDP",
  "Etsy",
  "Shopify / direct",
  "Creative Market",
  "Multi-channel",
] as const;

export const formatOptions = [
  "Low-content paperback",
  "Guided journal",
  "Activity book",
  "Workbook",
  "Planner",
  "Printable PDF",
] as const;

export const ImprintLockSchema = z.object({
  name: z.string().max(80),
  positioning: z.string().max(600),
  primaryColor: z.string().regex(/^#[0-9a-f]{6}$/i),
  secondaryColor: z.string().regex(/^#[0-9a-f]{6}$/i),
  accentColor: z.string().regex(/^#[0-9a-f]{6}$/i),
  typographyMood: z.string().max(180),
  coverRules: z.string().max(1000),
  interiorRules: z.string().max(1000),
  voiceRules: z.string().max(1000),
  metadataRules: z.string().max(1000),
});

export const CatalogueBookSchema = z.object({
  id: z.string().min(1),
  sequence: z.number().int().positive(),
  type: z.string().min(1),
  title: z.string().max(180),
  subtitle: z.string().max(240),
  differentiator: z.string().max(500),
  theme: z.string().max(180),
  status: z.enum(["planned", "drafting", "ready"]),
});

export const PromptCustomizationSchema = z.object({
  targetBookId: z.string().optional(),
  customInstructions: z.string().max(1500).default(""),
});

export const PromptWorkSchema = z.object({
  copiedAt: z.string().max(40).default(""),
  response: z.string().max(50000).default(""),
  status: z
    .enum(["not-started", "needs-review", "approved", "skipped"])
    .default("not-started"),
  approvedAt: z.string().max(40).default(""),
  useInLaterSteps: z.boolean().default(false),
});

export const ProductionInteriorPlanSchema = z.object({
  layoutIds: z.array(z.string()).max(24).default([]),
  moduleIds: z.array(z.string()).max(48).default([]),
  sequenceNotes: z.string().max(2000).default(""),
});

export const ProductionCoverPlanSchema = z.object({
  variant: z.string().max(120).default(""),
  titleTreatment: z.string().max(600).default(""),
  subtitleTreatment: z.string().max(600).default(""),
  focalBrief: z.string().max(1200).default(""),
  backCoverCopy: z.string().max(2400).default(""),
  differentiationNote: z.string().max(1200).default(""),
});

export const RightsLedgerItemSchema = z.object({
  id: z.string().min(1),
  asset: z.string().min(1).max(180),
  source: z.string().max(500),
  licence: z.string().max(240),
  status: z.enum(["to-check", "cleared", "restricted"]),
  notes: z.string().max(1000),
});

export const ProofLogItemSchema = z.object({
  id: z.string().min(1),
  bookId: z.string().min(1),
  reviewedAt: z.string().max(40),
  outcome: z.enum(["pending", "changes-needed", "approved"]),
  notes: z.string().max(1500),
});

export const CustomCoverPaletteSchema = z.object({
  colors: z
    .array(
      z.object({
        name: z.string().max(60),
        hex: z.string().regex(/^#[0-9a-f]{6}$/i),
      }),
    )
    .length(4)
    .default([
      { name: "Main colour", hex: "#29483B" },
      { name: "Background colour", hex: "#F6F1E7" },
      { name: "Text colour", hex: "#252A27" },
      { name: "Accent colour", hex: "#C9734A" },
    ]),
});

export const CustomerFileChecksSchema = z.object({
  interiorPdfOpens: z.boolean().default(false),
  interiorSizeMatches: z.boolean().default(false),
  pageOrderChecked: z.boolean().default(false),
  fontsEmbedded: z.boolean().default(false),
  coverPdfOpens: z.boolean().default(false),
  coverDimensionsMatch: z.boolean().default(false),
  barcodeAreaClear: z.boolean().default(false),
});

export const defaultCustomerFileChecks = {
  interiorPdfOpens: false,
  interiorSizeMatches: false,
  pageOrderChecked: false,
  fontsEmbedded: false,
  coverPdfOpens: false,
  coverDimensionsMatch: false,
  barcodeAreaClear: false,
};

export const ProductionStateSchema = z.object({
  version: z.literal(1).default(1),
  coverSystemId: z.string().max(100).default(""),
  coverPaletteId: z.string().max(100).default(""),
  customCoverPalette: CustomCoverPaletteSchema.default({
    colors: [
      { name: "Main colour", hex: "#29483B" },
      { name: "Background colour", hex: "#F6F1E7" },
      { name: "Text colour", hex: "#252A27" },
      { name: "Accent colour", hex: "#C9734A" },
    ],
  }),
  coverHierarchy: z.string().max(1200).default(""),
  backCoverFramework: z.string().max(1800).default(""),
  coverNotes: z.string().max(2000).default(""),
  coverPlans: z.record(z.string(), ProductionCoverPlanSchema).default({}),
  interiorPlans: z.record(z.string(), ProductionInteriorPlanSchema).default({}),
  trimSize: z.string().max(80).default("6 × 9 in"),
  paperType: z.string().max(80).default("White paper"),
  bleed: z.enum(["No bleed", "Bleed"]).default("No bleed"),
  confirmedSpineWidth: z.string().max(80).default(""),
  barcodeTreatment: z
    .string()
    .max(240)
    .default("Reserve the current marketplace barcode-safe area"),
  typographySpecification: z.string().max(1200).default(""),
  plannedPageCount: z.number().int().min(24).max(1000).default(120),
  primaryFileCreated: z.boolean().default(false),
  primaryFilePromptSignature: z.string().max(100).default(""),
  interiorPdfFileName: z.string().max(240).default(""),
  interiorPageCount: z.number().int().nonnegative().max(5000).default(0),
  coverDimensions: z.string().max(120).default(""),
  coverPdfCreated: z.boolean().default(false),
  coverFilePromptSignature: z.string().max(100).default(""),
  coverPdfFileName: z.string().max(240).default(""),
  customerFileChecks: CustomerFileChecksSchema.default(defaultCustomerFileChecks),
  exportSettings: z
    .string()
    .max(1200)
    .default(""),
  channelDelivery: z.record(z.string(), z.string().max(1200)).default({}),
  rightsLedger: z.array(RightsLedgerItemSchema).max(100).default([]),
  proofLog: z.array(ProofLogItemSchema).max(100).default([]),
  preflight: z.record(z.string(), z.boolean()).default({}),
  revisionNotes: z.string().max(3000).default(""),
});

export const ListingRecordSchema = z.object({
  listingTitle: z.string().max(240).default(""),
  description: z.string().max(5000).default(""),
  keywords: z
    .array(z.string().max(80))
    .max(13)
    .default(["", "", "", "", "", "", "", "", "", "", "", "", ""]),
  categories: z.array(z.string().max(180)).max(3).default(["", "", ""]),
  audienceDetails: z.string().max(1200).default(""),
  metadataNotes: z.string().max(1600).default(""),
  seoTitle: z.string().max(240).default(""),
  seoDescription: z.string().max(500).default(""),
  shortPitch: z.string().max(800).default(""),
  socialPosts: z
    .array(z.string().max(1600))
    .max(3)
    .default(["", "", ""]),
  launchEmailSubject: z.string().max(240).default(""),
  launchEmailBody: z.string().max(4000).default(""),
  visualPlan: z
    .array(z.string().max(1200))
    .max(5)
    .default(["", "", "", "", ""]),
});

export const APlusModuleSchema = z.object({
  id: z.string().min(1),
  storyboardId: z.string().min(1),
  headline: z.string().max(240),
  body: z.string().max(2000),
  imageBrief: z.string().max(1200),
});

export const LaunchGrowthStateSchema = z.object({
  version: z.literal(1).default(1),
  activeBookId: z.string().optional(),
  listings: z.record(z.string(), ListingRecordSchema).default({}),
  listingsByMarketplace: z
    .record(z.string(), z.record(z.string(), ListingRecordSchema))
    .default({}),
  aPlusModules: z.array(APlusModuleSchema).max(5).default([]),
  evergreenPlanId: z.string().min(1).default("single-title-first"),
  launchDate: z.string().max(40).default(""),
  authorCentralComplete: z.boolean().default(false),
  channelAccountReady: z.record(z.string(), z.boolean()).default({}),
  adObjective: z.string().max(600).default(""),
  adBudgetNotes: z.string().max(600).default(""),
  launchNotes: z.string().max(2400).default(""),
  leadMagnetId: z.string().min(1).default("quick-start-companion"),
  qrDestination: z.string().max(600).default(""),
  insertCopy: z.string().max(1200).default(""),
  optInHeadline: z.string().max(240).default(""),
  optInBody: z.string().max(1600).default(""),
  welcomeSequenceId: z.string().min(1).default("useful-first-week"),
  crossSellTemplateIds: z.array(z.string()).max(12).default([]),
  funnelNotes: z.string().max(2400).default(""),
  funnelEnabled: z.boolean().default(false),
});

export type ProductionState = z.infer<typeof ProductionStateSchema>;
export type CustomerFileChecks = z.infer<typeof CustomerFileChecksSchema>;
export type ProductionCoverPlan = z.infer<typeof ProductionCoverPlanSchema>;
export type ProductionInteriorPlan = z.infer<typeof ProductionInteriorPlanSchema>;
export type RightsLedgerItem = z.infer<typeof RightsLedgerItemSchema>;
export type ProofLogItem = z.infer<typeof ProofLogItemSchema>;
export type ListingRecord = z.infer<typeof ListingRecordSchema>;
export type APlusModule = z.infer<typeof APlusModuleSchema>;
export type LaunchGrowthState = z.infer<typeof LaunchGrowthStateSchema>;
export type PromptWork = z.infer<typeof PromptWorkSchema>;

export function createDefaultProductionState(): ProductionState {
  return ProductionStateSchema.parse({});
}

export function createDefaultLaunchGrowthState(): LaunchGrowthState {
  return LaunchGrowthStateSchema.parse({});
}

export const PaperKingdomProjectSchema = z.object({
  id: z.string().min(1),
  ownerEmail: z.string().email(),
  schemaVersion: z.literal(1),
  productVersion: z.string().min(1),
  name: z.string().min(1).max(120),
  revision: z.number().int().nonnegative(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  marketplace: z.enum(marketplaceOptions),
  audience: z.string().max(180),
  format: z.enum(formatOptions),
  marketPackId: z.string().min(1),
  visualDirectionId: z.string().min(1),
  theme: z.string().max(240),
  bookType: z.string().max(120),
  catalogueSize: z.number().int().min(1).max(24),
  imprint: ImprintLockSchema,
  catalogue: z.array(CatalogueBookSchema).max(24),
  promptPlanIds: z.array(z.string()),
  favoritePromptIds: z.array(z.string()),
  promptCustomizations: z.record(z.string(), PromptCustomizationSchema).default({}),
  promptWork: z.record(z.string(), PromptWorkSchema).default({}),
  lastCompleteHandoffAt: z.string().max(40).default(""),
  notes: z.string().max(5000),
  production: ProductionStateSchema.default(createDefaultProductionState),
  launchGrowth: LaunchGrowthStateSchema.default(createDefaultLaunchGrowthState),
});

export type ImprintLock = z.infer<typeof ImprintLockSchema>;
export type CatalogueBook = z.infer<typeof CatalogueBookSchema>;
export type PromptCustomization = z.infer<typeof PromptCustomizationSchema>;
export type PaperKingdomProject = z.infer<typeof PaperKingdomProjectSchema>;

export const AuditEntrySchema = z.object({
  id: z.string(),
  actorEmail: z.string().email(),
  targetEmail: z.string().email(),
  action: z.enum(["grant", "revoke", "invite"]),
  offerSku: OfferSkuSchema.optional(),
  createdAt: z.string().datetime(),
});

export type AuditEntry = z.infer<typeof AuditEntrySchema>;

export const PortalSnapshotSchema = z.object({
  schemaVersion: z.literal(1),
  session: z
    .object({
      email: z.string().email(),
      role: z.enum(["buyer", "admin"]),
    })
    .nullable(),
  entitlements: z.record(z.string(), z.array(OfferSkuSchema)),
  projects: z.array(PaperKingdomProjectSchema),
  auditLog: z.array(AuditEntrySchema),
});

export type PortalSnapshot = z.infer<typeof PortalSnapshotSchema>;

export const ProductManifestSchema = z.object({
  schemaVersion: z.literal(1),
  productSlug: z.literal("paper-kingdom"),
  productVersion: z.string(),
  name: z.string(),
  description: z.string(),
  theme: z.object({
    paper: z.string(),
    ink: z.string(),
    forest: z.string(),
    brass: z.string(),
  }),
  offers: z.array(
    z.object({
      sku: OfferSkuSchema,
      name: z.string(),
      price: z.number().positive(),
    }),
  ),
  counts: z.object({
    corePrompts: z.number().int().positive(),
    starterMarkets: z.number().int().positive(),
    starterModules: z.number().int().positive(),
    visualDirections: z.number().int().positive(),
  }),
});

export type ProductManifest = z.infer<typeof ProductManifestSchema>;

export function createProject(ownerEmail: string, name = "My first book project"): PaperKingdomProject {
  const now = new Date().toISOString();
  return {
    id: crypto.randomUUID(),
    ownerEmail,
    schemaVersion: 1,
    productVersion: "1.1.0",
    name,
    revision: 0,
    createdAt: now,
    updatedAt: now,
    marketplace: "Amazon KDP",
    audience: "",
    format: "Guided journal",
    marketPackId: "mindful-living",
    visualDirectionId: "modern-botanical",
    theme: "",
    bookType: "Guided journal",
    catalogueSize: 1,
    imprint: {
      name: "",
      positioning: "",
      primaryColor: "#29463a",
      secondaryColor: "#e6dcc7",
      accentColor: "#c97855",
      typographyMood: "",
      coverRules: "",
      interiorRules: "",
      voiceRules: "",
      metadataRules: "",
    },
    catalogue: createCatalogueRows(1, "Guided journal", ""),
    promptPlanIds: ["pk-title-01", "pk-cover-01", "pk-interior-01", "pk-description-01"],
    favoritePromptIds: [],
    promptCustomizations: {},
    promptWork: {},
    lastCompleteHandoffAt: "",
    notes: "",
    production: createDefaultProductionState(),
    launchGrowth: createDefaultLaunchGrowthState(),
  };
}

export function createCatalogueRows(
  count: number,
  type: string,
  theme: string,
  previous: CatalogueBook[] = [],
): CatalogueBook[] {
  return Array.from({ length: count }, (_, index) => {
    const existing = previous[index];
    return (
      existing ?? {
        id: crypto.randomUUID(),
        sequence: index + 1,
        type,
        title: "",
        subtitle: "",
        differentiator: "",
        theme: theme ? `${theme} ${index + 1}` : "",
        status: "planned" as const,
      }
    );
  }).map((book, index) => ({ ...book, sequence: index + 1, type }));
}
